<template>
    <form @submit.prevent="onSubmit">
      <div class="p-2">
        
        <div class="mb-6 space-y-4">
          <!-- Request Type GET/POST(JSON) -->
          <label class="block w-full">
            <span class="text-sm font-medium text-gray-700">
              {{ $t('INTEGRATION_SETTINGS.CLICK2CALL.REQUEST_TYPE_LABEL') }}
            </span>
            <select
              v-model="method"
              :disabled="!hasEditPermission('Click2Call')"
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-woot-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="GET">Get</option>
              <option value="POST">Post (JSON)</option>
            </select>
          </label>
  
      <!-- BASE URL -->
          <label class="block w-full">
            <span class="text-sm font-medium text-gray-700">
              {{ $t('INTEGRATION_SETTINGS.CLICK2CALL.BASE_URL_LABEL') }}
            </span>
            <input
              :disabled="!hasEditPermission('Click2Call')"
              v-model.trim="baseUrl"
              type="text"
              maxlength="1024"
              class="mt-1 block w-full px-3 py-2 border border-woot-100 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </label>
        </div>
  
          <!-- Select a Parameter Dropdown -->
        <div class="relative mb-0 " style="display: grid; grid-template-columns: 14rem 1fr;">
          <div class="text-left rounded-md border border-woot-100 w-auto" style="align-self: start;"> 
  
              <button 
              type="button"
              class="inline-flex px-3 py-2 text-sm font-semibold text-gray-90 hover:bg-slate-50 dark:hover:bg-slate-900 w-full" 
              id="options-menu"
              @click="toggleMainDropdown"
              :disabled="!hasEditPermission('Click2Call')"
              >
                <span class="mr-2 text-ellipsis text-sm text-slate-800 dark:text-slate-100 bg-transparent select-none">{{ $t('INTEGRATION_SETTINGS.CLICK2CALL.BUTTON_PLACAEHOLDER') }} </span>
                <i class="icon ion-chevron-down ml-auto"/>
              </button>
  
            <div
              v-if="mainDropdownOpen"
              class="ml-0 w-56 origin-top-right"
              role="menu"
              >
              <ul class="list-none py-1">
                <li 
                  v-for="(item, index) in menu" 
                  :key="index" 
                  class="relative group py-1 hover:bg-slate-50 dark:hover:bg-slate-900"
                  @click="openSubmenu(index)"
                >
                  <div class="flex items-center justify-between w-full">
                    <h4 class="flex items-center justify-between w-full mt-0 mb-0 mr-2 ml-0 text-ellipsis text-sm text-slate-800 dark:text-slate-100 text-left">
                          {{ item.label }}
                    </h4>
                      <i
                        :class="['h-4 w-4 icon',isRTL ? 'ion-chevron-left mr-2' : 'ion-chevron-right ml-2']"
                        style="font-size: 10px;"
                      ></i>
                  </div>
                </li>
              </ul>
            </div>
          </div>
  
          <!-- SUBLIST -->
          <div 
            v-if="activeItem"
            class="relative inline-block text-left rounded-md border border-woot-100 w-auto"
            style="align-self: start;"> 
            <ul
              role="menu"
              style="max-height: 192px; overflow-y: auto;"
              class="list-none top-0 left-full ml-1 w-full rounded-md bg-white group-hover:block"
              >
  
                  <li
                    v-for="(subItem, subIndex) in activeItem.children"
                    :key="subIndex"
                    class="ml-0 block px-4 py-2 text-ellipsis text-sm text-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
                    @click="selectKey(activeItem.label, subItem.key)"
                    role="menuitem"
                  >
                    <h4 class="flex items-center justify-between w-full mt-0 mb-0 mr-2 ml-0 text-ellipsis text-sm text-slate-800 dark:text-slate-100 text-left">
                      {{ subItem.display }}
                    </h4>
                  </li>
            </ul>
          </div>
  
          </div>
          
        </div>
  
  
        <!-- Render selected parameters -->
        <div class="relative parameter-list mt-0">
          <div
            v-for="(param, index) in selectedParameters"
            :key="index"
            >
            <div class="flex justify-between items-center mb-2">
              <label class="block font-bold text-gray-800">
                {{ $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_COUNTER_LABEL') }} {{ index + 1 }}
              </label>
              <woot-button
                variant="clear"
                color-scheme="secondary"
                icon="dismiss"
                @click="removeParameter(index)"
                title="Remove parameter"
              />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">{{ $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_PAIRS.KEY') }}:</label>
                <input
                  :disabled="!hasEditPermission('Click2Call')"
                  v-model="param.key"
                  type="text"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">{{ $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_PAIRS.VALUE') }}:</label>
                <input
                  :value="`{{${param.value}}}`"
                  type="text"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
  
        <div class="flex flex-row justify-end gap-2 py-4 px-0">
          <woot-button variant="clear" @click.prevent="onCancel">
            {{ $t('MERGE_CONTACTS.FORM.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :disabled="!isValidForm || !hasEditPermission('Click2Call')" :is-loading="isSubmitting">
            {{ $t('PLATFORM.FORM.SUBMIT') }}
          </woot-button>
        </div>
      </div>
      <div v-if="isLoading" class="dd-loader">
        <spinner />
      </div>
    </form>
  </template>
  
  <script>
  import alertMixin from 'shared/mixins/alertMixin';
  import Click2CallSettingsAPI from '../../../../../api/click2callSettings';
  import Spinner from 'shared/components/Spinner.vue';
  import hasPermissionMixin from 'dashboard/mixins/hasPermission';
  import AttributeAPI from 'dashboard/api/attributes';
  
  export default {
    name: 'Click2CallSettings',
    mixins: [alertMixin, hasPermissionMixin],
    components: {
      Spinner,
    },
    props: {},
    data() {
      return {
        menu: [],
        mainDropdownOpen: false, 
        activeIndex: null, 
        activeItem: null,
        method: '',
        selectedParameters: [], 
        baseUrl: undefined,
        isSubmitting: false,
        isLoading: false,
        contactFields: [
        { display: 'Name', key: 'name' },
        { display: 'Phone Number (+972)', key: 'phone_number_+972' },
        { display: 'Phone Number (972)', key: 'phone_number_972' },
        { display: 'Phone Number (05)', key: 'phone_number_05' },
        { display: 'Company', key: 'company_name' },
        { display: 'Email', key: 'email' },
      ],
      };
    },
    validations: {
      // Add validations if needed for baseUrl, etc.
    },
    mounted() {
      this.fetchCustomAttributeDefinitions();
      this.load();
    },
    computed: {
      isRTL() {
        return this.$i18n.locale === 'he' || this.$i18n.locale === 'ar';
      },
      translatedContactFields() {
        return this.contactFields.map(field => ({
          key: field.key,
          display: this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.CONTACT_FIELDS.${field.key}`),
        }));
      },
      isValidForm() {
        return (
          !!this.baseUrl &&
          !!this.method &&
          this.selectedParameters.length > 0
        );
      },
    },
    methods: {
      hasDuplicateKeys() {
        if(!this.selectedParameters)
         return false;
        const keys = this.selectedParameters?.map(p => p.key);
        const uniqueKeys = new Set(keys);
        return uniqueKeys.size !== keys.length;
      },
      contactProfileLink() {
        return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
      },
      // sort custom attributes into groups and create a fields menu
      buildMenuFromCustomAttributes() {
        const grouped = {};
  
        this.customAttributes.forEach(attr => {
          const model = attr.attribute_model;
  
          if (!grouped[model]) {
            grouped[model] = {
              label: this.formatLabel(model),
              children: [],
            };
          }
  
          grouped[model].children.push({
            display: attr.attribute_display_name,
            key: attr.attribute_key,
          });
        });
        const dynamicGroups = Object.values(grouped);
        const contactFieldsGroup = {
          label: this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.CONTACT_FIELDS.DISPLAY`),
          children: this.translatedContactFields,
        };
        this.menu = [...dynamicGroups, contactFieldsGroup];
        // this.menu = Object.values(grouped);
      },
      formatLabel(model) {
        const i18nKey = `INTEGRATION_SETTINGS.CLICK2CALL.ATTRIBUTES.${model}`;
        const translated = this.$t(i18nKey);
        if (translated === i18nKey) {
          // Converts snake_case like 'agent_attribute' to 'Agent Attribute'
          return model
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
        }
        return translated;
  
      },
      async fetchCustomAttributeDefinitions() {
        const response = await AttributeAPI.getAttributesByModel();
        this.customAttributes = response.data;
        this.buildMenuFromCustomAttributes();
  
      },
      // Toggles the main dropdown visibility
      toggleMainDropdown() {
        this.mainDropdownOpen = !this.mainDropdownOpen;
  
        // Close any open submenu when main dropdown is toggled
        if (!this.mainDropdownOpen) {
          this.activeIndex = null;
          this.activeItem = null;
        }
      },
      openSubmenu(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null;
        this.activeItem = null;
        return;
      }
      this.activeIndex = index;
      this.activeItem = this.menu[index];
      },
  
      selectKey(groupLabel, selectedKey) {
        if (!selectedKey) {
          return;
        }
  
        // Check if the parameter already exists in the selectedParameters array
        const isDuplicate = this.selectedParameters.some(param => param.key === selectedKey);
        if (isDuplicate) {
          this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.DUPLICATE_ERROR`));
          return;
        }
  
        this.selectedParameters.push({
          key: selectedKey,
          value: selectedKey,
        });
  
      },
      // Removes a parameter from the selected list
      removeParameter(index) {
        this.selectedParameters.splice(index, 1);
      },
  
      onCancel() {
        this.$emit('cancel');
      },
  
      async onSubmit() {
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }
        if (this.hasDuplicateKeys()) {
          this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.DUPLICATE_KEYS_ERROR`));
          return;
        }
        const payload = {
          enable: true,
          base_url: this.baseUrl,
          method: this.method,
          selected_parameters: this.selectedParameters.map(param => ({
            key: param.key,
            value: param.value,
          })),
        };
  
        this.isSubmitting = true;
        try{
          const response = await Click2CallSettingsAPI.setSettings(payload);
  
          this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.SEND_SUCCESS`));
          this.onCancel();
        } catch (err) {
          console.error(err);
          this.showAlert(this.$t('PLATFORM.API.SAVE_ERROR'));
        } finally{
          this.isSubmitting = false;
        }
      },
  
      async load() {
        this.isLoading = true;
        try {
          const { data } = await Click2CallSettingsAPI.getSettings();
          this.baseUrl = data?.base_url || undefined;
          this.method = data?.method || '';
          this.selectedParameters = data.selected_parameters || [];
        } catch (err) {
          console.error(err);
          this.showAlert(this.$t('PLATFORM.API.GET_ERROR'));
        } finally {
          this.isLoading = false;
        }
      },
    },
  };
  </script>
  
  